import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Compass, Search, X, SlidersHorizontal, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  ExploreGrid,
  FilterSheet,
  ColumnPicker,
  ExploreActionBar,
  AddToListMenu,
} from '../components/explore';
import { SegmentPills } from '../components/shared/SegmentPills';
import {
  useExploreStore,
  addSelectedToList,
  addAllMatchingToList,
  saveSmartList,
  updateSmartFilter,
} from '../store/exploreStore';
import { EXPLORE_TABS } from '../lib/exploreColumns';
import type { ListEntityKind, FilterSpec } from '../types/list';

type ExploreIntent = 'standalone' | 'add-to-manual' | 'edit-smart';

interface ExploreLocationState {
  intent?: ExploreIntent;
  listId?: string;
  listName?: string;
  entityKind?: ListEntityKind;
  initialFilterSpec?: FilterSpec;
}

// Which add path the controlled AddToListMenu is resolving (so onPick knows what to do).
type PickerTarget = 'selected' | 'all' | null;

/**
 * Explore — Lists v2 dense filter-table page.
 *
 * Composes the per-kind grid (ExploreGrid), filters (FilterSheet on phone /
 * column header controls on desktop), the column picker, and the action bar.
 * Behaviour is driven by an *intent* carried in router state:
 *   - standalone   (Lists "New from search")   → Save as smart list
 *   - add-to-manual (ListDetail "Add items")    → add straight to the carried list
 *   - edit-smart    (ListDetail "Edit filter")  → Save filter (PATCH) the carried list
 */
export function Explore() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? {}) as ExploreLocationState;
  const intent: ExploreIntent = state.intent ?? 'standalone';
  const { listId, listName } = state;

  const {
    kind,
    filterSpec,
    pagination,
    selected,
    visibleColumns,
    setKind,
    setKindAndFilter,
    setFilter,
    clearFilter,
    setVisibleColumns,
    resetSelection,
    fetchPreview,
  } = useExploreStore();

  // Any filter axis set beyond the version marker? Drives the "Clear filters" affordance.
  const hasFilters = Object.entries(filterSpec).some(
    ([k, v]) => k !== 'version' && v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0),
  );
  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    clearFilter();
  }, [clearFilter]);

  // --- Local UI state -------------------------------------------------------
  const [searchInput, setSearchInput] = useState(filterSpec.search ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Controlled AddToListMenu (standalone / edit-smart list picker)
  const [pickerTarget, setPickerTarget] = useState<PickerTarget>(null);

  // Inline "Save as smart list" name composer (standalone)
  const [naming, setNaming] = useState(false);
  const [smartName, setSmartName] = useState('');
  const [savingSmart, setSavingSmart] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // --- On mount (once): load the right tab + filters ------------------------
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (intent === 'edit-smart' && state.entityKind) {
      setKindAndFilter(state.entityKind, state.initialFilterSpec ?? { version: 1 });
    } else {
      fetchPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (naming) nameInputRef.current?.focus();
  }, [naming]);

  // --- Clean up pending search debounce on unmount -------------------------
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  // --- Debounced search -----------------------------------------------------
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setFilter({ search: value || undefined });
      }, 300);
    },
    [setFilter],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFilter({ search: undefined });
  }, [setFilter]);

  const matching = pagination?.totalCount ?? 0;

  // --- Add paths ------------------------------------------------------------
  const doAddSelected = useCallback(
    async (targetListId: string) => {
      try {
        const added = await addSelectedToList(targetListId, selected);
        toast.success(listName ? `Added ${added} to ${listName}` : `Added ${added}`);
        resetSelection();
      } catch {
        toast.error('Could not add the selected items.');
      }
    },
    [selected, listName, resetSelection],
  );

  const doAddAll = useCallback(
    async (targetListId: string) => {
      try {
        const added = await addAllMatchingToList(targetListId, kind, filterSpec);
        toast.success(listName ? `Added all ${added} to ${listName}` : `Added all ${added}`);
        resetSelection();
      } catch {
        toast.error('Could not add the matching items.');
      }
    },
    [kind, filterSpec, listName, resetSelection],
  );

  // Action-bar callbacks. For add-to-manual the target list is known → act
  // directly. Otherwise open the controlled AddToListMenu so the rep can pick
  // or create a manual list first.
  const handleAddSelected = useCallback(() => {
    if (intent === 'add-to-manual' && listId) void doAddSelected(listId);
    else setPickerTarget('selected');
  }, [intent, listId, doAddSelected]);

  const handleAddAll = useCallback(() => {
    if (intent === 'add-to-manual' && listId) void doAddAll(listId);
    else setPickerTarget('all');
  }, [intent, listId, doAddAll]);

  const handlePickerPick = useCallback(
    (pickedId: string) => {
      const target = pickerTarget;
      setPickerTarget(null);
      if (target === 'selected') void doAddSelected(pickedId);
      else if (target === 'all') void doAddAll(pickedId);
    },
    [pickerTarget, doAddSelected, doAddAll],
  );

  // --- Save smart list (standalone) ----------------------------------------
  const handleSaveSmart = useCallback(() => {
    setSmartName('');
    setNaming(true);
  }, []);

  const handleConfirmSaveSmart = useCallback(async () => {
    const trimmed = smartName.trim();
    if (!trimmed || savingSmart) return;
    setSavingSmart(true);
    try {
      const { list } = await saveSmartList(trimmed, kind, filterSpec);
      toast.success(`Saved smart list “${list.name}”`);
      navigate(`/lists/${list.id}`);
    } catch {
      toast.error('Could not save the smart list.');
    } finally {
      setSavingSmart(false);
      setNaming(false);
    }
  }, [smartName, savingSmart, kind, filterSpec, navigate]);

  // --- Save filter (edit-smart) --------------------------------------------
  const handleSaveFilter = useCallback(async () => {
    if (!listId) return;
    try {
      await updateSmartFilter(listId, filterSpec);
      toast.success('Filter updated');
      navigate(`/lists/${listId}`);
    } catch {
      toast.error('Could not update the filter.');
    }
  }, [listId, filterSpec, navigate]);

  // Tab pills — single-select, blast-lime active via SegmentPills. Counts are
  // unknown per-tab, so show the live total on the active tab and '—' elsewhere.
  const tabPills = EXPLORE_TABS.map((t) => ({
    key: t.key,
    label: t.label,
    count: t.key === kind ? matching : null,
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-lime-100 dark:bg-lime-900/30 rounded-lg flex-shrink-0">
              <Compass className="h-6 w-6 text-lime-600 dark:text-lime-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Explore</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Filter and sort across orgs, programs, and people — then build a list
              </p>
            </div>
          </div>
        </div>

        {/* Add-to-manual banner */}
        {intent === 'add-to-manual' && listName && (
          <div className="bg-lime-50 dark:bg-lime-900/20 border border-lime-200 dark:border-lime-800 rounded-2xl px-4 py-3 flex items-center gap-2 text-sm">
            <span className="text-gray-600 dark:text-gray-300">Adding to</span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">{listName}</span>
          </div>
        )}

        {/* Tab switcher */}
        <SegmentPills pills={tabPills} activeKey={kind} onSelect={(k) => setKind(k as ListEntityKind)} />

        {/* Search + columns + (phone) filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="input pl-10 w-full"
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Filters — phone only (desktop filters live in the grid header) */}
            <button
              type="button"
              className="btn-secondary text-sm flex items-center gap-1.5 md:hidden"
              onClick={() => setFilterSheetOpen(true)}
            >
              <SlidersHorizontal size={16} />
              Filters
            </button>
            <ColumnPicker kind={kind} visible={visibleColumns} onChange={setVisibleColumns} />
          </div>
        </div>

        {/* Action bar — sticky on phone only; picker anchors to this wrapper */}
        <div className="relative max-md:sticky max-md:bottom-0 max-md:z-30 bg-white dark:bg-gray-900">
          <ExploreActionBar
            matching={matching}
            selectedCount={selected.size}
            intent={intent}
            listName={listName}
            hasFilters={hasFilters}
            onClearFilters={handleClearFilters}
            onAddSelected={handleAddSelected}
            onAddAll={handleAddAll}
            onSaveSmart={handleSaveSmart}
            onSaveFilter={handleSaveFilter}
          />

          {/* Controlled list picker for standalone / edit-smart add paths.
              Anchors bottom-right of this wrapper so it follows the bar on phone. */}
          {intent !== 'add-to-manual' && (
            <div className="absolute right-3 bottom-full mb-1 z-30">
              <AddToListMenu
                onPick={handlePickerPick}
                open={pickerTarget !== null}
                onOpenChange={(o) => { if (!o) setPickerTarget(null); }}
              />
            </div>
          )}
        </div>

        {/* Inline "Save as smart list" name composer (standalone) */}
        {intent === 'standalone' && naming && (
          <div className="card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              <Star size={16} className="text-lime-600 dark:text-lime-400" />
              Save this filter as a smart list
            </div>
            <input
              ref={nameInputRef}
              type="text"
              value={smartName}
              onChange={(e) => setSmartName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleConfirmSaveSmart();
                if (e.key === 'Escape') setNaming(false);
              }}
              placeholder="List name"
              className="input text-sm flex-1"
              disabled={savingSmart}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-primary text-sm disabled:opacity-50"
                onClick={() => void handleConfirmSaveSmart()}
                disabled={!smartName.trim() || savingSmart}
              >
                {savingSmart ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => setNaming(false)}
                disabled={savingSmart}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Grid */}
        <ExploreGrid />
      </div>

      {/* Filters bottom-sheet (phone) */}
      <FilterSheet open={filterSheetOpen} onClose={() => setFilterSheetOpen(false)} />
    </div>
  );
}
